
import { useState, useEffect, useRef } from 'react';
import { trackButtonClick } from '../../../utils/stats';

const staticMessages = [
  {
    role: 'user' as const,
    text: '你们的智能客服目前支持哪些场景啊？',
  },
  {
    role: 'bot' as const,
    text: '我们支持网页/APP/小程序/H5/电商平台/企业微信/内容平台等多种接入场景。',
  },
  {
    role: 'user' as const,
    text: '企业微信如何接入？具体怎么操作？',
  },
  {
    role: 'bot' as const,
    text: '在企业微信后台-应用管理中，搜索“火花派智能客服系统”，点击添加试用。之后将有专人联系，协助完成知识库搭建和工作流配置，快速上手使用。',
  },
];

function ChatBubble({ role, text }: { role: 'user' | 'bot'; text: string }) {
  const isUser = role === 'user';
  return (
    <div className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div
        className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm ${
          isUser
            ? 'bg-gradient-to-br from-[#0066ff] to-[#5b4cff]'
            : 'bg-gradient-to-br from-[#7c5cfc] to-[#a78bfa]'
        }`}
      >
        {isUser ? <i className="ri-user-3-line text-xs" /> : <i className="ri-robot-2-line text-xs" />}
      </div>
      <div
        className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm leading-relaxed shadow-sm ${
          isUser
            ? 'bg-gradient-to-br from-[#0066ff] to-[#5b4cff] text-white rounded-br-sm'
            : 'bg-white/80 backdrop-blur-sm text-gray-800 rounded-bl-sm border border-white/60'
        }`}
      >
        {text}
      </div>
    </div>
  );
}

function StaticChat({ onShowQRCode }: { onShowQRCode: () => void }) {
  return (
    <div className="relative w-full max-w-[400px] mx-auto">
      <div className="absolute -top-8 -left-8 w-40 h-40 bg-[#0066ff]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-[#7c5cfc]/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative bg-white/40 backdrop-blur-xl rounded-2xl border border-white/60 shadow-2xl shadow-blue-200/30 overflow-hidden">
        {/* 顶部标题栏 */}
        <div className="flex items-center gap-2.5 px-4 py-3.5 bg-gradient-to-r from-[#0066ff]/90 to-[#5b4cff]/90 backdrop-blur-sm">
          <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-robot-2-line text-white text-base" />
          </div>
          <div>
            <div className="text-white text-base font-bold leading-tight">智能客服助手</div>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
              <span className="text-white/80 text-xs">在线</span>
            </div>
          </div>
          <div className="ml-auto flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-white/30" />
            <div className="w-2.5 h-2.5 rounded-full bg-white/30" />
            <div className="w-2.5 h-2.5 rounded-full bg-white/30" />
          </div>
        </div>

        {/* 对话内容 */}
        <div className="px-3 py-3 space-y-3 " style={{ height: '320px' }}>
          {staticMessages.map((msg, i) => (
            <ChatBubble key={i} role={msg.role} text={msg.text} />
          ))}
        </div>

        {/* 输入框 */}
        <div className="px-3 pb-3">
          <div 
            onClick={() => {
              trackButtonClick('int_y_q', '问题');
              onShowQRCode();
            }}
            className="flex items-center gap-2 bg-white/60 backdrop-blur-sm rounded-xl border border-white/70 px-3 py-2.5 shadow-sm cursor-pointer hover:bg-white/80 hover:border-blue-300 transition-all"
          >
            <span className="flex-1 text-sm text-gray-400 min-h-[20px]">输入您的问题...</span>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                trackButtonClick('int_y_q', '问题');
                onShowQRCode();
              }}
              className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-[#0066ff] to-[#5b4cff] rounded-lg flex-shrink-0 cursor-pointer"
            >
              <i className="ri-send-plane-fill text-white text-sm" />
            </button>
          </div>
        </div>
      </div>

      <div className="absolute -top-3 -right-3 bg-gradient-to-br from-[#0066ff] to-[#7c5cfc] text-white text-[10px] font-semibold px-2.5 py-1 rounded-full shadow-lg shadow-blue-300/40 whitespace-nowrap">
        AI 秒级响应
      </div>
    </div>
  );
}

export default function Hero() {
  const [showQRCode, setShowQRCode] = useState(false);
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!hasTracked.current) {
      hasTracked.current = true;
      trackButtonClick('page_view', '首页页面加载');
    }
    const handleLoad = () => {
      trackButtonClick('page_viewed', '首页页面加载完成');
    };
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []);

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#eaf2ff] via-[#f0eeff] to-[#e8f4fc]"></div>

      <div className="absolute top-16 left-8 w-72 h-72 bg-blue-200/25 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-purple-200/25 rounded-full blur-3xl"></div>
      <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-violet-200/20 rounded-full blur-2xl"></div>
      <div className="absolute bottom-1/4 left-1/4 w-56 h-56 bg-blue-100/30 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 md:pb-24">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 w-full">
          <div className="flex-1 w-full text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 bg-white/70 backdrop-blur-sm rounded-full mb-4 sm:mb-5 border border-purple-100">
              <span className="w-2 h-2 bg-[#7c5cfc] rounded-full animate-pulse"></span>
              <span className="text-xs sm:text-sm text-[#7c5cfc] font-medium">AI驱动新一代客服系统</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-5 leading-tight">
              真正解决问题的<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0066ff] to-[#7c5cfc]">智能客服</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-5 sm:mb-7 leading-relaxed">
              接入只是开始，效果才是目的
            </p>

            <div className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
              <div className="flex items-start gap-2.5 sm:gap-3 justify-center lg:justify-start">
                <span className="text-lg sm:text-xl">🎯</span>
                <span className="text-sm sm:text-base text-gray-700">支持网页/APP/小程序/H5接入</span>
              </div>
              <div className="flex items-start gap-2.5 sm:gap-3 justify-center lg:justify-start">
                <span className="text-lg sm:text-xl">🧠</span>
                <span className="text-sm sm:text-base text-gray-700">支持私域企业微信接入</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 relative justify-center lg:justify-start">
              <button
                onClick={() => {
                  trackButtonClick('try_now_btn', '首页Hero区域马上试用按钮');
                  setShowQRCode(true);
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 bg-gradient-to-r from-[#0066ff] to-[#5b4cff] text-white text-sm sm:text-base font-medium rounded-lg hover:shadow-xl transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                马上试用
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>

          <div className="hidden lg:flex flex-1 items-center justify-center">
            <StaticChat onShowQRCode={() => setShowQRCode(true)} />
          </div>
        </div>
      </div>

      {showQRCode && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
          onClick={() => setShowQRCode(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">扫码免费体验</h3>
              <button
                onClick={() => setShowQRCode(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="bg-gray-100 p-4 sm:p-6 rounded-xl">
              <img
                src="https://static.readdy.ai/image/c9656b810d2c6cc0a8672cac16e7cf96/e7f95b852e2d7fc033ed8e2278921757.png"
                alt="QR Code"
                className="w-full h-auto"
              />
            </div>
            <p className="text-center text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4">使用微信扫描二维码</p>
          </div>
        </div>
      )}
    </section>
  );
}
